import recognitionService from 'app/services/recognition';
import { endLoading, startLoading, setErrors, reviewLoading } from 'app/app-dux/app-dux';
import { PERSON_STORAGE_KEY, USER_STORAGE_KEY, LANDING_PAGES } from '../../constants';
import { getSelectedPeople } from 'app/app-dux/people-dux';
import { getSelectedBehavior, getSelectedProgram } from 'app/app-dux/program-dux';
import { getMessage, getSelectedEcard, getVisibility } from 'app/app-dux/message-dux';
import { message } from '@nackle/paper';
import { getString } from '@nackle/intl-tools';

/*******************************************************************************
 * Initial state
 */
const initialState = {
	balance: 0,
	menuItems: [],
	branding: {}
};

/*******************************************************************************
 * Non-async action types
 */
export const SET_POINT_BALANCE = 'SET_POINT_BALANCE';
export const SET_MENU_ITEMS = 'SET_MENU_ITEMS';
export const CLEAR_RECOGNITION = 'setState_CLEAR_RECOGNITION';
export const SET_BRANDING = 'setState_SET_BRANDING';

/*******************************************************************************
 * Reducer
 */
export default function recognitionReducer( state = initialState, action ) {
	switch ( action.type ) {
		case SET_BRANDING: {
			return {
				...state,
				branding: action.branding
			};
		}
		case SET_POINT_BALANCE: {
			return {
				...state,
				balance: action.balance
			};
		}
		case SET_MENU_ITEMS: {
			return {
				...state,
				menuItems: action.menuItems
			};
		}
		default:
			return state;
	}
}

/*******************************************************************************
 * Action creators
 */
export const setBranding = branding => ( { type: SET_BRANDING, branding } );
export const setPointBalance = balance => ( { type: SET_POINT_BALANCE, balance } );
export const setMenuItems = menuItems => ( { type: SET_MENU_ITEMS, menuItems } );
export const clearRecognition = () => ( { type: CLEAR_RECOGNITION } );

export const getPointBalance = () => async ( dispatch ) => {
	dispatch ( startLoading() );
	try {
		const response = await recognitionService.getPointBalance( localStorage.getItem( PERSON_STORAGE_KEY ) );
		if ( response.success ) {
			dispatch( setPointBalance(  response?.balance ) );
		}
	} catch ( error ) {
		throw error;
	} finally {
		dispatch( endLoading() );
	}
};

export const getMenuItems = () => async dispatch => {
	let user = localStorage.getItem( USER_STORAGE_KEY );
	user = user ? JSON.parse( user ) : {};
	const person = {
		id: user.claims?.person_id,
		country: user.claims?.country
	};
	dispatch ( startLoading() );
	try {
		const response = await recognitionService.getMenuItems( person );
		console.info( 'menu items:', response );
		if ( response ) {
			dispatch( setMenuItems( response ) );
			return true;
		}
	} catch ( error ) {
		throw error;
	} finally {
		dispatch( endLoading() );
	}
};

export const getLogosAndColors = () => async dispatch => {
	dispatch ( startLoading() );
	try {
		const response = await recognitionService.getLogosAndColors();
		if ( response ) {
			dispatch( setBranding( response ) );
			return response;
		}
	} catch ( error ) {
		throw error;
	} finally {
		dispatch( endLoading() );
	}
};

export const getMarketplaceUrl = ( pageName ) => async dispatch => {
	let landingPage = LANDING_PAGES[ pageName && pageName.toUpperCase() ];

	let user = localStorage.getItem( USER_STORAGE_KEY );
	user = user ? JSON.parse( user ) : {};
	const person = {
		id: user.claims?.person_id,
		country: user.claims?.country
	};
	dispatch ( startLoading() );
	try {
		const shopUrl = await recognitionService.getMarketplaceUrl( person, landingPage );
		console.info( 'marketplace response:', shopUrl );
		if ( shopUrl ) {
			return shopUrl;
		}
	} catch ( error ) {
		// Handling error in main-header component
		return null;
	} finally {
		dispatch( endLoading() );
	}
};

export const submitRecognition = ( source, submitState ) => {
	return async ( dispatch, getState ) => {
		dispatch( setErrors( [] ) );
		let authState = localStorage.getItem( 'user' );
		if ( authState ) {
			authState = JSON.parse( authState );
		} else {
			message.error( getString( 'recognition.error.generic', { error: 'Something went wrong with authentication' } ) );
		}
		submitState === 'VALIDATED' ? dispatch( reviewLoading( true ) ) : dispatch( startLoading() );
		const state = getState();
		const recognition = {
			senderPersonId: authState.claims.person_id,
			source,
			state: submitState,
			recipients: buildRecipientList( state ),
			sharedSelections: buildSharedSelections( state )
		};
		return recognitionService.createRecognition( recognition )
			.then( data => {
				submitState === 'VALIDATED' ? dispatch( reviewLoading( false ) ) : dispatch( endLoading() );
				dispatch( setErrors( data.errors ) );
				return data;
			} ).catch( error => {
				console.error( error );
				submitState === 'VALIDATED' ? dispatch( reviewLoading( false ) ) : dispatch( endLoading() );
				dispatch( setErrors(  error.errors ) );
				return error;
			} );
	};
};

const buildRecipientList =  ( state ) => {
	const selectedPeople = getSelectedPeople( state );
	let recipientList = [];
	for ( let i = 0; i < selectedPeople.length; i++ ) {
		const recipient = {
			personId: selectedPeople[ i ].id,
			selections: selectedPeople[ i ].awardValue ? buildPointsReward( selectedPeople[ i ].awardValue, state ) : []
		};
		recipientList.push( recipient );
	}
	return recipientList;
};

const buildPointsReward = ( awardValue, state ) => {
	const { id, rewardChoiceId, budgets } = selectedProgram( state );
	let selections = [];
	const pointsAward = {
		type: 'REWARD',
		programId: id,
		rewardChoiceId: rewardChoiceId,
		data: {
			rewardType: 'POINTS',
			points: {
				value: parseInt( awardValue )
			},
			deliveryType: 'DIRECT',
			budgets: [
				{
					budgetId: budgets[ 0 ].id,
					value: parseInt( awardValue )
				}
			]
		}
	};
	selections.push( pointsAward );
	return selections;
};

const buildSharedSelections = ( state ) => {
	const { id, rewardChoiceId } = selectedProgram( state );
	const message = getMessage( state );
	const selectedBehavior = getSelectedBehavior( state );
	const selectedEcard = getSelectedEcard( state );
	const visibility = getVisibility( state );
	const sharedSelections = [];
	const messageSelection = {
		type: 'MESSAGE',
		data: { text: message || '' },
		programId: id,
		rewardChoiceId: rewardChoiceId
	};
	sharedSelections.push( messageSelection );

	if ( selectedBehavior?.id ) {
		const behaviorSelection = {
			type: 'BEHAVIOR',
			programId: id,
			rewardChoiceId: rewardChoiceId,
			data: {
				behaviorId: selectedBehavior?.id
			}
		};
		sharedSelections.push( behaviorSelection );
	}
	if ( selectedEcard && selectedEcard.id ) {
		const ecardSelection = {
			type: 'REWARD',
			programId: id,
			rewardChoiceId: rewardChoiceId,
			data: {
				rewardType: 'ECARD',
				ecard: {
					ecardId: selectedEcard.id
				},
				deliveryType: 'DIRECT'
			}

		};
		sharedSelections.push( ecardSelection );
	}
	const privacySelection = {
		type: 'VISIBILITY',
		programId: id,
		rewardChoiceId: rewardChoiceId,
		data: {
			value: visibility.toUpperCase()
		}
	};
	sharedSelections.push( privacySelection );
	return sharedSelections;
};

// selectors
const selectedProgram = ( state ) => getSelectedProgram( state );
